import { useMutation, useQueryClient } from '@tanstack/react-query'
import { archiveAccount, type AccountSummary } from '@/api/accounts'
import { Button } from '@/components/ui/button'

export function ArchiveAccountDialog({
  account,
  onClose,
}: {
  account: AccountSummary | null
  onClose: () => void
}) {
  const queryClient = useQueryClient()

  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveAccount(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['accounts'] }),
    onError: (err: { response?: { data?: { detail?: string } } }) => {
      alert(err?.response?.data?.detail ?? 'Failed to archive account. Please try again.')
    },
  })

  if (!account) return null

  const activeAccount = account

  function handleConfirm() {
    archiveMutation.mutate(activeAccount.id)
    onClose()
  }

  return (
    <div
      role="dialog"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
    >
      <div className="w-full max-w-sm rounded-xl bg-[var(--fl-surface)] p-6 shadow-[rgba(0,0,0,0.15)_0px_8px_40px]">
        <h3 className="mb-2 font-['Sora',system-ui,sans-serif] text-base font-semibold text-[var(--fl-text-1)]">
          Archive &quot;{account.name}&quot;?
        </h3>
        <p className="mb-4 text-sm text-[var(--fl-text-2)]">
          It&apos;ll be removed from your account list and net worth. Its past transactions are
          kept, and it can no longer be edited or receive new transactions.
        </p>
        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleConfirm}>
            Archive
          </Button>
        </div>
      </div>
    </div>
  )
}
